import React, { useEffect, useRef } from "react";
import Chart from "chart.js";

// 1. Définition de l'interface pour les éléments de données
interface DoughnutDataItem {
  label: string;
  amount: number;
}

// 2. Définition de l'interface pour les Props du composant
interface CardDoughnutChartProps {
  title: string;
  data: DoughnutDataItem[];
  loading?: boolean; // Optionnel avec '?'
}

export default function CardDoughnutChart({
  title,
  data,
  loading = false,
}: CardDoughnutChartProps) {
  // 3. Typage des Refs (HTMLCanvasElement pour le canvas, Chart pour l'instance)
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (chartRef.current && data.length > 0 && !loading) {
      const ctx = chartRef.current.getContext("2d");

      if (ctx) {
        if (chartInstance.current) {
          chartInstance.current.destroy();
        }

        chartInstance.current = new Chart(ctx, {
          type: "doughnut",
          data: {
            labels: data.map((item) => item.label),
            datasets: [
              {
                data: data.map((item) => item.amount),
                backgroundColor: [
                  "#D4AF37",
                  "#38BDF8",
                  "#34D399",
                  "#F471B5",
                  "#818CF8",
                  "#FBBF24",
                ],
                borderColor: "#1E293B",
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            // Propriété spécifique à Chart.js v2 pour le cercle intérieur
            cutoutPercentage: 70,
            legend: {
              display: true,
              position: "bottom",
              labels: {
                fontColor: "#94A3B8",
                fontSize: 11,
                padding: 20,
                usePointStyle: true,
              },
            },
            tooltips: {
              callbacks: {
                // Typage des arguments du tooltip pour éviter les erreurs TS
                label: function (tooltipItem: any, chartData: any) {
                  const dataset = chartData.datasets[tooltipItem.datasetIndex];
                  const value = dataset.data[tooltipItem.index];
                  return ` ${Number(value).toLocaleString()} FCFA`;
                },
              },
            },
          },
        });
      }
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [data, loading]);

  return (
    <div className="relative flex flex-col min-w-0 break-words bg-slate-800 w-full mb-6 shadow-xl rounded-2xl border border-slate-700">
      <div className="rounded-t mb-0 px-6 py-4 bg-transparent">
        <div className="flex flex-wrap items-center">
          <div className="relative w-full max-w-full flex-grow flex-1">
            <h6 className="uppercase text-slate-400 mb-1 text-xs font-bold">
              Répartition mensuelle
            </h6>
            <h2 className="text-white text-xl font-bold">{title}</h2>
          </div>
        </div>
      </div>
      <div className="p-6 flex-auto">
        <div className="relative h-[300px]">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <i className="fas fa-circle-notch animate-spin text-[#D4AF37] text-3xl"></i>
            </div>
          ) : data.length > 0 ? (
            <canvas ref={chartRef} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center text-slate-500 italic text-sm">
              <i className="fas fa-chart-pie text-4xl mb-3 opacity-20"></i>
              Aucune donnée collectée ce mois-ci
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
